/**
 * Questionnaire completion rules shared by the onboarding survey and the
 * edit-portrait flow: what counts as answered, and how the answers are saved.
 */
import registrationService from '../api/services/registrationService';
import { STORAGE_KEYS } from '../constants/storage';
import { storage } from '../platform/storage';
import { getApiErrorMessage } from './apiErrorHandler';
import { toWeightKg } from './weightUtils';
import {
  mapSurveyToBuddyPreferences,
  BUDDY_PREFERENCES_UPDATED_EVENT,
} from '../api/onboarding/onboardingMapper';
import type { ApiError } from '../types/api';
import type { SurveyState } from '../types/survey';

export { BUDDY_PREFERENCES_UPDATED_EVENT, mapSurveyToBuddyPreferences };

/** True once the member has answered at least one survey question. */
export const hasExistingQuestionnaireData = (survey: SurveyState): boolean =>
  Boolean(
    survey.mainGoal ||
      survey.poundsToLose != null ||
      survey.commitmentLevel ||
      survey.buddyType ||
      survey.communicationStyle ||
      survey.checkInFrequency ||
      survey.genderPreference ||
      survey.ageRange ||
      Object.values(survey.personalizationRatings ?? {}).some(
        value => value != null && value > 0,
      ),
  );

/**
 * The six fields the registration contract requires. Readiness ratings are
 * optional, so they must never block a save.
 */
export const canCompleteQuestionnaire = (survey: SurveyState): boolean => {
  const targetWeightLossKg =
    survey.poundsToLose != null ? toWeightKg(Number(survey.poundsToLose), 'lbs') : null;

  return (
    survey.mainGoal != null &&
    survey.commitmentLevel != null &&
    survey.buddyType != null &&
    survey.communicationStyle != null &&
    survey.checkInFrequency != null &&
    targetWeightLossKg != null &&
    Number.isFinite(targetWeightLossKg) &&
    targetWeightLossKg >= 0.1
  );
};

/**
 * Checks whether the user's saved buddy preferences questionnaire data contains all required fields.
 */
export const isBuddyPreferencesComplete = (
  prefs?: import('../types/userProfile').GetUserBuddyPreferencesResponse | null,
): boolean => {
  if (!prefs) return false;

  const hasGoalAndWeight =
    Boolean(prefs.mainGoalId) &&
    prefs.targetWeightLossKg !== null &&
    prefs.targetWeightLossKg !== undefined;

  const hasPreferences =
    Boolean(prefs.commitmentLevelId) &&
    Boolean(prefs.preferredBuddyTypeId) &&
    Boolean(prefs.communicationStyleId) &&
    Boolean(prefs.checkInFrequencyId) &&
    prefs.preferredBuddyGenderId != null &&
    prefs.preferredBuddyAgeMin !== null &&
    prefs.preferredBuddyAgeMin !== undefined &&
    prefs.preferredBuddyAgeMax !== null &&
    prefs.preferredBuddyAgeMax !== undefined;

  const hasRatings =
    Boolean(prefs.ratingMotivationToWeightLoss) &&
    Boolean(prefs.ratingConsistentHealthyHabit) &&
    Boolean(prefs.ratingAccountability) &&
    Boolean(prefs.ratingProgressSharingComfortLevel) &&
    Boolean(prefs.ratingCheckInFrequency) &&
    Boolean(prefs.ratingEmotionalSupportImportance);

  return hasGoalAndWeight && hasPreferences && hasRatings;
};

export interface SaveQuestionnaireResult {
  success: boolean;
  /** Set when the save was rejected; already user-readable. */
  message?: string;
  /** True when required answers are missing, so the caller can keep the user in the flow. */
  incomplete?: boolean;
}

/**
 * Sessions restored from storage — and logins from before the GUID was
 * persisted — can leave AuthContext without a GUID, so fall back to the keys
 * written at sign-up, login and profile fetch.
 */
const resolveUserGUID = async (userGUID: string | null | undefined): Promise<string | null> => {
  if (userGUID) return userGUID;

  const storedGuid = await storage.getItem<string>(STORAGE_KEYS.USER_GUID);
  if (storedGuid) return storedGuid;

  const rawProfile = await storage.getItem<string>(STORAGE_KEYS.USER_PROFILE);
  if (!rawProfile) return null;

  try {
    const parsed = typeof rawProfile === 'string' ? JSON.parse(rawProfile) : rawProfile;
    const guid: string | undefined = parsed?.userGuid;
    if (!guid) return null;
    await storage.setItem(STORAGE_KEYS.USER_GUID, guid);
    return guid;
  } catch {
    return null;
  }
};

/** Submits the answers for an existing member editing their portrait. */
export const saveQuestionnaire = async (
  survey: SurveyState,
  userGUID: string | null | undefined,
): Promise<SaveQuestionnaireResult> => {
  if (!canCompleteQuestionnaire(survey)) {
    return { success: false, incomplete: true };
  }

  const resolvedGuid = await resolveUserGUID(userGUID);
  if (!resolvedGuid) {
    return { success: false };
  }

  try {
    await registrationService.submitQuestionnaire(survey, resolvedGuid);
    return { success: true };
  } catch (caught) {
    return { success: false, message: getApiErrorMessage(caught as ApiError) };
  }
};
